import * as React from "react";

import TrackingKeys from "@own-local/ad-utils/lib/trackingKeys";
import {
  AdRecord,
  Layout,
  TrackFunction,
  TranslateFunction,
} from "@own-local/ad-utils/lib/types";

import AboutContent from "./AboutContent";
import Background from "./Background";
import ContactContent from "./ContactContent";
import FacebookContent from "./FacebookContent";
import Header from "./Header";
import Imagiro from "./Imagiro";
import InfoContents from "./InfoContent";
import TabNav from "./TabNav";
import Ticker from "./Ticker";

import { ChevronUpIcon } from "./Icons";
import "./unit.scss";

export enum Tab {
  Info = 0,
  About = 1,
  Contact = 2,
  Facebook = 3,
}

interface AdUnitProps {
  data: AdRecord;
  layoutParam?: Layout;
  mapDisabled: boolean;
  track: TrackFunction;
  translateString: TranslateFunction;
  gamTrackingUrl?: string;
  selectedTab?: Tab;
  intialExpanded?: boolean;
  keepDocumentTitle?: boolean;
  showShareButtons?: boolean;
}

interface AdUnitState {
  expanded: boolean;
  shareModalExpanded: boolean;
  stretched: boolean;
  selectedTab: Tab;
  isAgent: boolean;
  tabCount: number;
  showInfoTab: boolean;
  showAboutTab: boolean;
  showContactTab: boolean;
  showFacebookTab: boolean;
  showFacebookWidget: boolean;
  showTwitterWidget: boolean;
  adShape: "horizontal" | "vertical" | "square";
}

export default class AdUnit extends React.Component<AdUnitProps, AdUnitState> {
  constructor(props: AdUnitProps) {
    super(props);
    this.state = {
      expanded: !!props.intialExpanded,
      shareModalExpanded: false,
      stretched: false,
      selectedTab: props.selectedTab || Tab.Info,

      isAgent: false,
      tabCount: 0,
      showInfoTab: false,
      showAboutTab: false,
      showContactTab: false,
      showFacebookTab: false,
      showFacebookWidget: false,
      showTwitterWidget: false,
      adShape: "square",
    };
  }

  public componentDidMount() {
    if (!this.props.keepDocumentTitle) document.title = this.props.data.name;
  }

  public componentDidUpdate(prevProps) {
    if (prevProps.selectedTab !== this.props.selectedTab)
      this.selectTab(this.props.selectedTab)();
  }

  public static calculateTabCount() {
    return Object.keys(arguments)
      .map((key) => arguments[key])
      .reduce((total, arg) => (arg ? total + 1 : total), 0);
  }

  public static getAdShape() {
    const height = window.innerHeight;
    const width = window.innerWidth;
    let shape;

    if (height > width) {
      shape = "vertical";
    } else if (width > height) {
      shape = "horizontal";
    } else if (height === width) {
      shape = "square";
    }

    return shape;
  }

  public static isAgent(categories) {
    return categories.some(
      (cat) => cat === "real-estate" || cat === "insurance"
    );
  }

  public static getDerivedStateFromProps(nextProps: AdUnitProps) {
    const {
      categories,
      about,
      contact,
      partnerContactEnabled,
      social,
    } = nextProps.data;
    const { email, place, phones } = contact;

    const showInfoTab = !!place.address1 || !!phones.length;
    const showAboutTab = !!about;
    const showContactTab = !!email && partnerContactEnabled;
    const showFacebookTab =
      "showFacebookTab" in social ? social.showFacebookTab : true;
    const tabCount = AdUnit.calculateTabCount();

    const isAgent = AdUnit.isAgent(categories);
    const adShape = AdUnit.getAdShape();

    return {
      isAgent,
      tabCount,
      showInfoTab,
      showAboutTab,
      showContactTab,
      showFacebookTab,
      adShape,
    };
  }

  public toggleExpandUnit = () => {
    this.setState({
      expanded: !this.state.expanded,
    });

    this.props.track(
      TrackingKeys.NAVIGATION.CATEGORY_NAME,
      TrackingKeys.NAVIGATION.ACTIONS.EXPAND,
      ""
    );
  };

  public selectTab = (tab: Tab) => () => {
    switch (tab) {
      case Tab.About:
        this.props.track(
          TrackingKeys.NAVIGATION.CATEGORY_NAME,
          TrackingKeys.NAVIGATION.ACTIONS.CHANGE_VIEW,
          "about-us"
        );
        break;
      case Tab.About:
        this.props.track(
          TrackingKeys.NAVIGATION.CATEGORY_NAME,
          TrackingKeys.NAVIGATION.ACTIONS.CHANGE_VIEW,
          "print-ad"
        );
        break;
      default:
        break;
    }

    this.setState({
      selectedTab: tab,
    });
  };

  public toggleShare = () => {
    this.setState({
      shareModalExpanded: !this.state.shareModalExpanded,
    });
  };

  public showFacebook = () => {
    this.setState({
      showFacebookWidget: true,
    });
  };

  public hideFacebook = () => {
    this.setState({
      showFacebookWidget: false,
    });
  };

  public showTwitter = () => {
    this.setState({
      showTwitterWidget: true,
    });
  };

  public hideTwitter = () => {
    this.setState({
      showTwitterWidget: false,
    });
  };

  public render() {
    const defaultLayout = "1";
    const {
      data,
      layoutParam,
      mapDisabled,
      track,
      translateString,
      gamTrackingUrl,
    } = this.props;
    const {
      headlines,
      name,
      directoryUrl,
      about,
      print,
      audio,
      contact,
      social,
      images,
      locale,
      layout,
      merlin,
    } = data;
    const {
      tabCount,
      showAboutTab,
      showContactTab,
      showInfoTab,
      showFacebookTab,
      isAgent,
    } = this.state;

    const filteredHeadlines = headlines.filter((headline) => !!headline.trim());

    let headlinesByLayout = filteredHeadlines;

    const adLayout = layoutParam || layout || defaultLayout;
    const isAudio = audio && audio.is;

    if (adLayout == "6" || adLayout == "7" || adLayout == "8") {
      headlinesByLayout = filteredHeadlines.slice(0, 1);
    }

    const baseAd = (
      <Imagiro
        expanded={this.state.expanded}
        shareModalExpanded={this.state.shareModalExpanded}
        stretched={this.state.stretched}
        tabCount={tabCount}
        tickerCount={headlinesByLayout.length}
        hasLogo={!!images.logo}
        isAgent={isAgent}
        layout={adLayout}
      >
        <div className="unit-outer">
          <Header image={images.logo} name={name} />
          <Background adShape={this.state.adShape} images={images} />
          <Ticker headlines={headlinesByLayout} />
          <div className="expand-button" onClick={this.toggleExpandUnit}>
            <div className="chevrons" />
          </div>
          <button className="expand-button-alt alt-button">
            Reveal details
          </button>
        </div>
        <div className="unit-inner">
          <div className="collapse-button" onClick={this.toggleExpandUnit}>
            <ChevronUpIcon />
          </div>
          <button className="expand-button-alt alt-button">Close</button>
          <div className="content">
            {(showAboutTab || showContactTab || showFacebookTab) && (
              <div className="content-nav">
                {showInfoTab && (
                  <TabNav
                    isAudio={isAudio}
                    tab={Tab.Info}
                    onClick={this.selectTab}
                    activeTab={this.state.selectedTab}
                    locale={locale}
                    translateString={translateString}
                  />
                )}
                {showAboutTab && (
                  <TabNav
                    isAudio={isAudio}
                    tab={Tab.About}
                    onClick={this.selectTab}
                    activeTab={this.state.selectedTab}
                    locale={locale}
                    translateString={translateString}
                  />
                )}
                {showContactTab && (
                  <TabNav
                    isAudio={isAudio}
                    tab={Tab.Contact}
                    onClick={this.selectTab}
                    activeTab={this.state.selectedTab}
                    locale={locale}
                    translateString={translateString}
                  />
                )}
                {showFacebookTab && (
                  <TabNav
                    isAudio={isAudio}
                    tab={Tab.Facebook}
                    onClick={this.selectTab}
                    activeTab={this.state.selectedTab}
                    locale={locale}
                    translateString={translateString}
                  />
                )}
                <div className="clear" />
              </div>
            )}
            <InfoContents
              contact={contact}
              social={social}
              active={this.state.selectedTab === Tab.Info}
              toggleShare={this.toggleShare}
              name={name}
              track={track}
              translateString={translateString}
              locale={locale}
              gamTrackingUrl={gamTrackingUrl}
              showFacebook={this.showFacebook}
              showTwitter={this.showTwitter}
              mapDisabled={mapDisabled}
              showShareButtons={this.props.showShareButtons}
            />
            <AboutContent
              active={this.state.selectedTab === Tab.About}
              name={name}
              about={about}
              print={print}
              audio={audio}
              translateString={translateString}
              locale={locale}
              gamTrackingUrl={gamTrackingUrl}
            />
            <ContactContent
              active={this.state.selectedTab === Tab.Contact}
              email={contact.email}
              name={name}
              headline={filteredHeadlines[0]}
              locale={locale}
              translateString={translateString}
            />
            <FacebookContent
              active={this.state.selectedTab === Tab.Facebook}
              facebookLink={social.facebook}
              gamTrackingUrl={gamTrackingUrl}
            />
          </div>
        </div>
      </Imagiro>
    );

    // This is the SelfServe Ad frame, to re-enable it, just uncomment this
    /* const ad = (
      <div className="merlin with-footer">
        <div className="merlin-unit">
          <div className="frame">{baseAd}</div>
          <a
            className="footer"
            href={`${directoryUrl}/promote?utm_source=origami&utm_medium=footer&utm_campaign=self-serve`}
            target="_blank"
          >
            <svg viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16V8C20.53,8.75 21.5,10.3 21.5,12Z"
              />
            </svg>
            Promote your business here
          </a>
        </div>
      </div>
    );

    return merlin ? ad : baseAd; */
    return baseAd;
  }
}
